package org.ttt.autogenesis.kvisionapp

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import org.ttt.autogenesis.network.RpcDirection
import org.ttt.autogenesis.network.RpcInvoker
import org.ttt.autogenesis.network.RpcRegistry
import org.ttt.autogenesis.network.WebSocketRpcClient
import org.ttt.autogenesis.network.WebSocketRpcClientConfig
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import globals.KEnv
import kotlin.math.absoluteValue
import kotlin.random.Random

/**
 * Manages a WebSocket-based RPC connection that targets the server module.
 *
 * Provides identical API to RestRpcBridge but uses WebSocket transport for
 * bidirectional real-time communication.
 */
object WebSocketRpcBridge
{
    private const val playerIdPrefix = "kvision-ws-client"
    private val rpcRegistry = RpcRegistry(RpcDirection.CLIENT)
    private var client : WebSocketRpcClient? = null
    private var currentPlayerId : String? = null
    private var currentAccelbyteId : String? = null
    private val connectedCallbacks = mutableListOf<() -> Unit>()
    private var connectionDeferred = CompletableDeferred<Unit>()

    /**
     * Returns the active invoker after a successful connection.
     */
    val rpcInvoker : RpcInvoker?
        get() = client?.rpcInvoker

    /**
     * Determines whether an active connection exists.
     */
    val isConnected : Boolean
        get() = client?.isConnected() == true

    /**
     * Determines whether the session is ready for RPC calls.
     */
    val isSessionReady : Boolean
        get() = client?.isSessionReady() == true

    /**
     * Returns the player id linked to the current WebSocket session.
     */
    val connectionId: String?
        get() = client?.connectionId

    /**
     * Registers a callback to run once the WebSocket connects.
     */
    fun onConnected(callback: () -> Unit)
    {
        connectedCallbacks.add(callback)
        if (isConnected)
        {
            callback()
        }
        client?.onConnected(callback)
    }

    /**
     * Suspends until the WebSocket connection is established and ready.
     */
    suspend fun waitForConnection()
    {
        if (isConnected) return
        connectionDeferred.await()
    }

    /**
     * Registers client-side RPC handlers so the server can invoke them.
     *
     * @param block Configuration block executed against the shared registry.
     */
    fun registerHandlers(block : RpcRegistry.() -> Unit)
    {
        block(rpcRegistry)
    }

    /**
     * Connects to the configured WebSocket endpoint using the provided player ID.
     *
     * @param playerId Optional player ID; a stable one is generated by default.
     * @param baseUrl WebSocket base URL (defaults to ws://127.0.0.1:9080)
     * @param accelbyteId Optional AccelByte user ID to assist server-side identification.
     */
    suspend fun connect(
        playerId : String = currentPlayerId ?: generatePlayerId(),
        baseUrl : String = "ws://127.0.0.1:9080",
        accelbyteId : String? = currentAccelbyteId
    )
    {
        Logger.info(LogCategory.NETWORK, "WebSocketRpcBridge: connect() invoked with playerId=$playerId accelbyteId=$accelbyteId target=$baseUrl")
        if (currentPlayerId == playerId && currentAccelbyteId == accelbyteId && isConnected)
        {
            Logger.info(LogCategory.NETWORK, "WebSocketRpcBridge: already connected as $playerId")
            return
        }

        if (connectionDeferred.isCompleted)
        {
            connectionDeferred = CompletableDeferred()
        }

        client?.let {
            it.disconnect()
            try
            {
                it.close()
            }
            catch (err: CancellationException)
            {
                throw err
            }
            catch (_: Throwable)
            {
                // Swallow errors while replacing the client
            }
        }

        val wsClient = WebSocketRpcClient(
            WebSocketRpcClientConfig(
                baseUrl = baseUrl,
                playerId = playerId,
                accelbyteId = accelbyteId,
                isGuestMode = KEnv.skipLogin
            ),
            rpcRegistry = rpcRegistry
        )
        
        wsClient.onConnected {
            Logger.info(LogCategory.NETWORK, "WebSocketRpcBridge: connection established for $playerId")
            if (!connectionDeferred.isCompleted)
            {
                connectionDeferred.complete(Unit)
            }
            connectedCallbacks.forEach { it() }
        }
        
        wsClient.connect()
        
        // Wait for the actual connection to be established before returning
        try {
            connectionDeferred.await()
            Logger.info(LogCategory.NETWORK, "WebSocketRpcBridge: successfully connected to $baseUrl as $playerId")
        } catch (e: Exception) {
            Logger.error(LogCategory.NETWORK, "WebSocketRpcBridge: failed to connect to $baseUrl: ${e.message}")
        }
        
        client = wsClient
        currentPlayerId = playerId
        currentAccelbyteId = accelbyteId
    }

    /**
     * Disconnects and releases any held client resources.
     */
    suspend fun close()
    {
        client?.let {
            it.disconnect()
            try
            {
                it.close()
            }
            catch (err: CancellationException)
            {
                throw err
            }
            finally
            {
                client = null
                currentPlayerId = null
                currentAccelbyteId = null
                if (!connectionDeferred.isCompleted)
                {
                    connectionDeferred.cancel()
                }
                connectionDeferred = CompletableDeferred()
            }
        }
    }

    private fun getConfigPlayerId() : String? {
        return try {
            if(kotlin.js.eval("typeof window.PLAYER_ID !== 'undefined' && window.PLAYER_ID !== null") as Boolean) {
                val id = kotlin.js.eval("window.PLAYER_ID") as String
                if(id.isNotBlank()) id else null
            } else {
                null
            }
        } catch(e: Throwable) {
            null
        }
    }

    private fun generatePlayerId() : String =
        getConfigPlayerId() ?: "$playerIdPrefix-${Random.nextInt().absoluteValue}"
}
